// src/components/LayerPanel/ReferenceTab.jsx
//
// REFERENCE scope. Static cartographic / legal boundary overlays the user
// turns on for orientation — they don't own them, act on them, or edit
// them. Survey grid, county boundaries, county names, and (future)
// plats / leases / PLSS belong here. The shape test for any future add:
//
//   • Static context toggled for orientation     → Reference
//   • Data/imagery the user operates on or edits → Imagery/Features/My Layers
//
// Interaction model:
//   • County Boundaries and Survey Grid are MUTUALLY EXCLUSIVE — at most
//     one on at a time. Turning one on forces the other off. Both-off is
//     allowed (clean map). They are NOT a radio group (radio would force
//     one always on).
//       Rationale: the two are different boundary frameworks (county
//       jurisdiction vs GLO land survey). They genuinely do not align —
//       showing both invites "why don't these match." MVP sidesteps that
//       by never showing both at once. NOTE for anyone extending this:
//       the GLO survey grid does NOT contain county boundaries; it is
//       the survey/title framework and has no county concept. County
//       identity comes from the county overlay or the name labels, never
//       from reading the survey grid.
//   • County Names is INDEPENDENT — its own toggle, on by default, not
//     coupled to either grid. Valid with county lines, with the survey
//     grid, or with neither (a label point is correct regardless of
//     which — or no — boundary framework is shown).
//
// Default first-load state: County Boundaries ON, County Names ON,
// Survey Grid OFF (county grid is the calmer default; the dense GLO
// section mesh is opt-in).
//
// This tab is toggles-only. No click-to-fly, no coverage linkage.

import { useGloStore } from "../../stores/useGloStore";
import { useCountyLayerStore } from "../../stores/useCountyLayerStore";
import { useCountyNamesStore } from "../../stores/useCountyNamesStore";
import ToggleSwitch from "./ToggleSwitch";

function cx(...c) { return c.filter(Boolean).join(" "); }

export default function ReferenceTab() {
  // Mutually-exclusive pair. We use the explicit setters (not toggles)
  // so we can drive both stores from one handler.
  const olsVisible = useGloStore((s) => s.olsVisible);
  const setOlsVisible = useGloStore((s) => s.setOlsVisible);

  const countiesVisible = useCountyLayerStore((s) => s.countiesVisible);
  const setCountiesVisible = useCountyLayerStore((s) => s.setCountiesVisible);

  // Independent — plain toggle, no coordination.
  const countyNamesVisible = useCountyNamesStore((s) => s.countyNamesVisible);
  const toggleCountyNames = useCountyNamesStore((s) => s.toggleCountyNames);

  // County Boundaries toggle: turning ON forces Survey Grid OFF.
  // Turning OFF just turns it off (both-off allowed).
  function handleToggleCounties() {
    const next = !countiesVisible;
    setCountiesVisible(next);
    if (next && olsVisible) setOlsVisible(false);
  }

  // Survey Grid toggle: symmetric — turning ON forces County
  // Boundaries OFF.
  function handleToggleOls() {
    const next = !olsVisible;
    setOlsVisible(next);
    if (next && countiesVisible) setCountiesVisible(false);
  }

  return (
    <div className="flex flex-col">
      <div className="px-2 py-2 space-y-2">
        <div className="space-y-1.5">
          <div className="flex items-center justify-between px-0.5">
            <span className="text-[10px] font-bold font-mono uppercase tracking-[0.14em] text-gray-400">
              Overlays
            </span>
            <span className="text-[9px] font-mono text-fg-secondary">3</span>
          </div>

          <div className="space-y-1">
            {/* Mutually-exclusive pair */}
            <Row
              label="County Boundaries"
              description="County jurisdiction lines, 7-state"
              on={countiesVisible}
              onToggle={handleToggleCounties}
            />
            <Row
              label="Survey Grid"
              description="GLO original land survey sections"
              on={olsVisible}
              onToggle={handleToggleOls}
            />

            {/* Independent — orientation labels, on by default */}
            <Row
              label="County Names"
              description="County name labels (works with either grid)"
              on={countyNamesVisible}
              onToggle={toggleCountyNames}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, description, on, onToggle }) {
  return (
    <div
      className={cx(
        "rounded-md border transition",
        on ? "bg-bg-softer border-slate-700" : "bg-bg border-slate-800 hover:border-slate-700"
      )}
    >
      <div className="flex items-center gap-1.5 px-2 py-[5px]">
        <span
          className={cx(
            "w-1.5 h-1.5 rounded-full flex-shrink-0",
            on ? "bg-accent-info" : "bg-transparent"
          )}
        />

        <button onClick={onToggle} className="flex-1 text-left min-w-0">
          <span
            className={cx(
              "text-[11px] block truncate",
              on ? "text-gray-200" : "text-gray-400 hover:text-gray-200"
            )}
          >
            {label}
          </span>
          {description && (
            <span className="text-[9px] text-fg-secondary block truncate">
              {description}
            </span>
          )}
        </button>

        <button onClick={onToggle} className="flex-shrink-0">
          <ToggleSwitch on={on} />
        </button>
      </div>
    </div>
  );
}

